import type { UIMessage } from 'ai'
import {
    isTextPart,
    isFilePart,
    isToolPart,
    isReasoningPart,
    hasPartText,
    hasTextPart,
    getTextPart,
    getMessageText,
} from 'utils'

export {
    isTextPart,
    isFilePart,
    isToolPart,
    isReasoningPart,
    hasPartText,
    hasTextPart,
    getTextPart,
    getMessageText,
}

/**
 * Merges consecutive text parts into a single text part.
 * Claude (and other models) often produce multiple text parts during streaming
 * that should logically be a single block of text.
 */
export function mergeConsecutiveTextParts(
    parts: UIMessage['parts'],
): UIMessage['parts'] {
    const merged: UIMessage['parts'] = []
    for (const part of parts) {
        if (!isTextPart(part)) {
            merged.push(part)
            continue
        }
        const prev = merged[merged.length - 1]
        if (prev && isTextPart(prev)) {
            merged[merged.length - 1] = { ...prev, text: prev.text + part.text }
        } else {
            merged.push(part)
        }
    }
    return merged
}

export function isStreamingPart(
    part: UIMessage['parts'][number] | null | undefined,
): part is Extract<UIMessage['parts'][number], { streaming?: boolean }> {
    if (!part) return false
    if ('state' in part) {
        return part.state === 'streaming' || part.state === 'input-streaming'
    }
    return false
}

/**
 * States a tool part goes through when it needs the user's approval. They are
 * rendered by `PkToolApprovalCard` whatever the tool is, so the decision (and
 * its outcome) stays visible in the transcript.
 */
const TOOL_APPROVAL_STATES = new Set([
    'approval-requested',
    'approval-responded',
    'output-denied',
])

export function isToolApprovalPart(
    part: UIMessage['parts'][number] | null | undefined,
): boolean {
    if (!isToolPart(part)) {
        return false
    }
    if (TOOL_APPROVAL_STATES.has(part.state)) {
        return true
    }
    // An executed (or errored) call that went through the gate keeps its
    // verdict in `approval`: the card stays in the transcript, so the user's
    // decision remains visible in the chat and in the log viewers (parity
    // with requestConfirm, whose 'confirmed' output is persisted).
    return (part as { approval?: unknown }).approval != null
}

/**
 * The client-side tool the model uses to have an action it described approved.
 * It predates the SDK's approval gate and keeps its own round-trip (it resolves
 * through `addToolOutput`), but it asks the user the very same question, so it
 * shares `PkToolApprovalCard` instead of having a card of its own.
 */
export const CONFIRM_TOOL_NAME = 'requestConfirm'

export function isToolConfirmPart(
    part: UIMessage['parts'][number] | null | undefined,
): boolean {
    return isToolPart(part) && part.type === `tool-${CONFIRM_TOOL_NAME}`
}

/** True for any part the confirmation card is responsible for. */
export function isConfirmationCardPart(
    part: UIMessage['parts'][number] | null | undefined,
): boolean {
    return isToolApprovalPart(part) || isToolConfirmPart(part)
}

/** The id of the pending approval request on a tool part, if any. */
export function getToolApprovalId(
    part: UIMessage['parts'][number] | null | undefined,
): string | undefined {
    if (!isToolPart(part)) {
        return undefined
    }
    return (part as { approval?: { id?: string } }).approval?.id
}

export function getPartState(
    part: UIMessage['parts'][number] | null | undefined,
): string | undefined {
    if (!part) return undefined
    if ('state' in part) {
        return part.state
    }
    return undefined
}

export function isLoadingState(state?: string): boolean {
    if (!state) return false
    return ['streaming'].includes(state)
}

export function getContrast(hexcolor: string = '000000'): 'black' | 'white' {
    let hex = hexcolor.replace('#', '')

    // Expand 3-character hex to 6-character (e.g., #ccc -> #cccccc)
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((char) => char + char)
            .join('')
    }

    // WCAG relative luminance
    const r = parseInt(hex.substring(0, 2), 16) / 255
    const g = parseInt(hex.substring(2, 4), 16) / 255
    const b = parseInt(hex.substring(4, 6), 16) / 255

    const linearize = (c: number) =>
        c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4

    const luminance =
        0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b)

    return luminance > 0.179 ? 'black' : 'white'
}

/**
 * Resolves the contrast color based on the user's `textColor` preference.
 * Falls back to WCAG auto-detection via `getContrast` when set to `'auto'` or `undefined`.
 */
export function resolveContrastColor(
    textColor: 'auto' | 'white' | 'black' | undefined,
    mainColor: string | undefined,
): 'black' | 'white' {
    if (textColor === 'white' || textColor === 'black') {
        return textColor
    }
    return getContrast(mainColor)
}

/**
 * Formats a duration in milliseconds using the i18n `seconds` number format
 * (e.g. "12,4 secondi" / "12.4 seconds"). Returns undefined for durations
 * that would render as "0 seconds" (less than 0.05s).
 */
export function formatDuration(
    $n: (value: number, format: string) => string,
    ms?: number,
): string | undefined {
    if (ms === undefined || ms < 50) {
        return undefined
    }
    return $n(ms / 1000, 'seconds')
}

/**
 * Stringifies a tool payload for the raw fallback view. Returns undefined for
 * empty values so the caller can skip rendering the block entirely.
 */
export function stringifyPayload(value: unknown): string | undefined {
    if (value === undefined) {
        return undefined
    }
    if (typeof value === 'string') {
        return value.trim() || undefined
    }
    try {
        const json = JSON.stringify(value, null, 2)
        return json === '{}' || json === '[]' ? undefined : json
    } catch {
        return undefined
    }
}

/**
 * True when a tool payload carries something worth rendering, so a caller can
 * skip the block without stringifying the value twice.
 */
export function hasPayload(value: unknown): boolean {
    return stringifyPayload(value) !== undefined
}

/**
 * Turns the answers of a form into the sentence the caller would have said.
 *
 * On a call the tool loop is over by the time the form is on screen, so the
 * answers go back to the agent as a user message (see `sendUserMessage` in
 * `useVoiceSession`). Each answer is paired with its question, because that is
 * how a person would say it and because the agent hears it with no widget
 * attached.
 */
export function formAnswerAsSpoken(
    part: unknown,
    answer: string | { answers: { id: string; value: unknown }[] },
): string {
    const input = (
        part as {
            input?: {
                steps?: { id: string; question?: string }[]
                question?: string
            }
        }
    )?.input

    if (typeof answer === 'string') {
        const question = input?.question ?? input?.steps?.[0]?.question
        return question ? `${question} ${answer}` : answer
    }

    const questionById = new Map(
        (input?.steps ?? []).map((step) => [step.id, step.question]),
    )
    const lines = answer.answers
        .filter(
            ({ value }) =>
                value !== null &&
                value !== '' &&
                !(Array.isArray(value) && !value.length),
        )
        .map(({ id, value }) => {
            const said = Array.isArray(value) ? value.join(', ') : String(value)
            const question = questionById.get(id)
            return question ? `${question} ${said}` : said
        })

    return lines.join('. ')
}

// Provider-native web search tool part types
export const WEB_SEARCH_TOOL_TYPES = [
    'tool-perplexity_search',
    'tool-parallel_search',
    'tool-web_search',
    'tool-google_search',
]

// Tool part types with a dedicated rich rendering in PkChatbotStepToolDetail
const RICH_DETAIL_TOOL_TYPES = new Set([
    'tool-searchInKnowledgeBase',
    'tool-retrieveRevisedAnswers',
    'tool-expandChunkContext',
    'tool-readDocumentFile',
    'tool-getWeather',
    'tool-geocode',
    'tool-recallUserMemory',
    'tool-saveUserMemory',
    ...WEB_SEARCH_TOOL_TYPES,
])

export function hasRichToolDetail(
    part?: UIMessage['parts'][number] | null,
): boolean {
    return isToolPart(part) && RICH_DETAIL_TOOL_TYPES.has(part.type)
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : undefined

const pickString = (
    record: Record<string, unknown> | undefined,
    ...keys: string[]
): string | undefined => {
    for (const key of keys) {
        const value = record?.[key]
        if (typeof value === 'string' && value.trim()) {
            return value
        }
    }
    return undefined
}

/**
 * Returns a short inline summary for a tool part (search queries, location,
 * document name, ...) to be shown next to the step label without expanding
 * the step body.
 */
export function getToolPartSummary(
    part?: UIMessage['parts'][number] | null,
): string | undefined {
    if (!isToolPart(part)) {
        return undefined
    }
    const input = asRecord('input' in part ? part.input : undefined)
    const output = asRecord('output' in part ? part.output : undefined)
    if (
        part.type === 'tool-searchInKnowledgeBase' ||
        WEB_SEARCH_TOOL_TYPES.includes(part.type)
    ) {
        const queries = input?.queries
        if (Array.isArray(queries)) {
            const valid = queries.filter(
                (query): query is string =>
                    typeof query === 'string' && Boolean(query.trim()),
            )
            return valid.length ? valid.join(' · ') : undefined
        }
        return pickString(input, 'query', 'objective')
    }
    if (part.type === 'tool-retrieveRevisedAnswers') {
        return pickString(input, 'query')
    }
    if (part.type === 'tool-getWeather') {
        return (
            pickString(output, 'location') ?? pickString(input, 'locationName')
        )
    }
    if (part.type === 'tool-geocode') {
        return pickString(input, 'locationName')
    }
    if (part.type === 'tool-expandChunkContext') {
        return pickString(output, 'documentName')
    }
    if (part.type === 'tool-readDocumentFile') {
        return pickString(output, 'name')
    }
    if (part.type === 'tool-recallUserMemory') {
        return pickString(input, 'query')
    }
    if (part.type === 'tool-saveUserMemory') {
        return pickString(input, 'content', 'query')
    }
    return undefined
}

// Get part icon
export function getPartIcon(
    part?: UIMessage['parts'][number] | null,
): string | undefined {
    if (isToolPart(part)) {
        if (part.type.includes('tool-subagent')) {
            return 'ri:robot-2-line'
        }
        if (part.type === 'tool-runWorker') {
            return 'ri:flow-chart'
        }
        if (part.type === 'tool-fetchTeamsChannelHistory') {
            return 'ri:chat-history-line'
        }
        if (part.type.includes('tool-mcp')) {
            return 'octicon:mcp-16'
        }
        if (part.type === 'tool-retrieveRevisedAnswers') {
            return 'ri:file-edit-line'
        }
        if (part.type === 'tool-searchInKnowledgeBase') {
            return 'ri:search-line'
        }
        if (part.type === 'tool-expandChunkContext') {
            return 'ri:file-search-line'
        }
        if (part.type === 'tool-readDocumentFile') {
            return 'ri:file-text-line'
        }
        if (part.type === 'tool-geocode') {
            return 'ri:map-2-line'
        }
        if (part.type === 'tool-getWeather') {
            return 'ri:sun-cloudy-line'
        }
        if (part.type === 'tool-recallUserMemory') {
            return 'ri:brain-line'
        }
        if (part.type === 'tool-saveUserMemory') {
            return 'ri:save-3-line'
        }
        if (part.type === 'tool-showWeather') {
            return 'ri:sun-line'
        }
        if (part.type === 'tool-showDiagram') {
            return 'ri:flow-chart'
        }
        if (part.type === 'tool-showSources') {
            return 'ri:book-2-line'
        }
        if (part.type === 'tool-showForm') {
            return 'ri:survey-line'
        }
        if (part.type === 'tool-showMultipleChoice') {
            return 'ri:checkbox-multiple-line'
        }
        if (part.type === 'tool-showArtifact') {
            return 'ri:file-code-line'
        }
        if (part.type === 'tool-showEmail') {
            return 'ri:mail-line'
        }
        if (part.type === 'tool-showMessage') {
            return 'ri:chat-1-line'
        }
        if (part.type === 'tool-showWebPages') {
            return 'ri:pages-line'
        }
        if (part.type === 'tool-showProductList') {
            return 'ri:shopping-bag-line'
        }
        if (part.type === 'tool-showContactForm') {
            return 'ri:send-plane-2-line'
        }
        if (part.type === 'tool-showCalendarEvent') {
            return 'ri:calendar-event-line'
        }
        if (part.type === 'tool-showComparison') {
            return 'ri:scales-line'
        }
        if (part.type === 'tool-showLocation') {
            return 'ri:map-pin-line'
        }
        if (part.type === 'tool-showQrCode') {
            return 'ri:qr-code-line'
        }
        if (part.type === 'tool-showImageGallery') {
            return 'ri:image-line'
        }
        if (part.type === 'tool-showVideo') {
            return 'ri:film-line'
        }
        if (part.type === 'tool-showSuggestedReply') {
            return 'ri:corner-down-right-line'
        }
        if (part.type === 'tool-requestGeolocation') {
            return 'ri:map-pin-user-line'
        }
        if (
            part.type === 'tool-perplexity_search' ||
            part.type === 'tool-parallel_search' ||
            part.type === 'tool-web_search' ||
            part.type === 'tool-google_search'
        ) {
            return 'ri:global-line'
        }
        return 'ri:tools-line'
    }
    if (isReasoningPart(part)) {
        return 'ri:lightbulb-line'
    }
    if (isTextPart(part)) {
        return 'ri:double-quotes-r'
    }
    return undefined
}

// Get part label
export function getToolPartLabel(
    $t: (key: string) => string,
    part?: UIMessage['parts'][number] | null,
): string | undefined {
    if (isToolPart(part)) {
        if (part.type.includes('tool-subagent')) {
            return $t(`messagePart.runSubagent`)
        }
        if (part.type === 'tool-runWorker') {
            return $t(`messagePart.runWorker`)
        }
        if (part.type === 'tool-fetchTeamsChannelHistory') {
            return $t(`messagePart.fetchTeamsChannelHistory`)
        }
        if (part.type === 'tool-retrieveRevisedAnswers') {
            return $t(`messagePart.revisedAnswers`)
        }
        if (part.type === 'tool-searchInKnowledgeBase') {
            return $t(`messagePart.searchingDocuments`)
        }
        if (part.type === 'tool-expandChunkContext') {
            return $t(`messagePart.expandChunkContext`)
        }
        if (part.type === 'tool-readDocumentFile') {
            return $t(`messagePart.readDocumentFile`)
        }
        if (part.type === 'tool-geocode') {
            return $t(`messagePart.geocode`)
        }
        if (part.type === 'tool-getWeather') {
            return $t(`messagePart.getWeather`)
        }
        if (part.type === 'tool-recallUserMemory') {
            return $t(`messagePart.recallUserMemory`)
        }
        if (part.type === 'tool-saveUserMemory') {
            return $t(`messagePart.saveUserMemory`)
        }
        if (part.type === 'tool-showWeather') {
            return $t(`messagePart.showWeather`)
        }
        if (part.type === 'tool-showForm') {
            return $t(`messagePart.showForm`)
        }
        if (part.type === 'tool-showDiagram') {
            return $t(`messagePart.showDiagram`)
        }
        if (part.type === 'tool-showSources') {
            return $t(`messagePart.showSources`)
        }
        if (part.type === 'tool-showMultipleChoice') {
            return $t(`messagePart.showMultipleChoice`)
        }
        if (part.type === 'tool-showArtifact') {
            return $t(`messagePart.showArtifact`)
        }
        if (part.type === 'tool-showEmail') {
            return $t(`messagePart.showEmail`)
        }
        if (part.type === 'tool-showMessage') {
            return $t(`messagePart.showMessage`)
        }
        if (part.type === 'tool-showWebPages') {
            return $t(`messagePart.showWebPages`)
        }
        if (part.type === 'tool-showProducts') {
            return $t(`messagePart.showProducts`)
        }
        if (part.type === 'tool-showContactForm') {
            return $t(`messagePart.showContactForm`)
        }
        if (part.type === 'tool-showCalendarEvent') {
            return $t(`messagePart.showCalendarEvent`)
        }
        if (part.type === 'tool-showComparison') {
            return $t(`messagePart.showComparison`)
        }
        if (part.type === 'tool-showLocation') {
            return $t(`messagePart.showLocation`)
        }
        if (part.type === 'tool-showQrCode') {
            return $t(`messagePart.showQrCode`)
        }
        if (part.type === 'tool-showImageGallery') {
            return $t(`messagePart.showImageGallery`)
        }
        if (part.type === 'tool-showVideo') {
            return $t(`messagePart.showVideo`)
        }
        if (part.type === 'tool-showSuggestedReply') {
            return $t(`messagePart.showSuggestedReply`)
        }
        if (part.type === 'tool-requestGeolocation') {
            return $t(`messagePart.requestGeolocation`)
        }
        if (WEB_SEARCH_TOOL_TYPES.includes(part.type)) {
            return $t(`messagePart.webSearch`)
        }
        return $t(`messagePart.tool`)
    }
    if (isReasoningPart(part)) {
        return $t(`messagePart.reasoning`)
    }
    return $t(`messagePart.streaming`)
}
